import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import supabase from '../../supabase';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import './Login.css'; // Import the new CSS file

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', variant: 'danger' });
  const [isResending, setIsResending] = useState(false);
  const [loading, setLoading] = useState(false); // State for loading indicator
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const desiredPlan = searchParams.get("plan");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotification({ show: false, message: '', variant: 'danger' }); // Reset notification

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        if (error.message === 'Email not confirmed') {
          setNotification({ 
            show: true, 
            message: "Votre compte n'est pas encore activé. Veuillez vérifier votre boîte de réception pour le lien de confirmation.", 
            variant: 'warning' 
          });
        } else {
          setNotification({ show: true, message: 'Email ou mot de passe incorrect.', variant: 'danger' });
        }
      } else if (data.user && data.session) {
        // Successful login
        if (desiredPlan === 'pro') {
          navigate('/paiement?plan=pro');
        } else {
          navigate('/properties');
        }
      }
    } catch (error) {
      setNotification({ show: true, message: 'Une erreur inattendue est survenue.', variant: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
      });
      if (error) {
        setNotification({ show: true, message: `Erreur lors de la connexion avec Google : ${error.message}`, variant: 'danger' });
      }
      // Supabase handles redirection, so no navigate() call is needed here.
    } catch (error) {
      setNotification({ show: true, message: 'Une erreur inattendue est survenue.', variant: 'danger' });
    } finally {
      // Don't set loading to false here, as the page will redirect.
    }
  };

  const handleResendConfirmation = async () => {
    setIsResending(true);
    setNotification({ show: false, message: '', variant: 'danger' });

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
    });

    if (error) {
      setNotification({ show: true, message: `Erreur lors du renvoi : ${error.message}`, variant: 'danger' });
    } else {
      setNotification({ show: true, message: 'Un nouvel e-mail de confirmation a été envoyé. Veuillez vérifier votre boîte de réception (et vos spams).', variant: 'success' });
    }
    setIsResending(false);
  };

  return (
    <Container className="login-container">
      <Row className="justify-content-center">
        <Col>
          <Card className="login-card">
            <Card.Body>
              <h2 className="text-center login-title">Connexion</h2>
              
              {notification.show && (
                <Alert variant={notification.variant} onClose={() => setNotification({ ...notification, show: false })} dismissible>
                  {notification.message}
                  {notification.variant === 'warning' && (
                    <div className="mt-2">
                      <Button 
                        variant="link" 
                        onClick={handleResendConfirmation} 
                        disabled={isResending}
                        className="p-0"
                      >
                        {isResending ? 'Envoi en cours...' : "Renvoyer l'e-mail de confirmation"}
                      </Button>
                    </div>
                  )}
                </Alert>
              )}

              <Form onSubmit={handleLogin}>
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Entrez votre email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formPassword">
                  <Form.Label>Mot de passe</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Entrez votre mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                </Form.Group>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-100 login-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="ms-2">Connexion...</span>
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </Form>
              <div className="text-center mt-3">
                <p>
                  Pas encore inscrit ?{' '}
                  <Button variant="link" onClick={() => navigate('/signup')} className="signup-link-button" disabled={loading}>
                    Créer un compte
                  </Button>
                </p>
              </div>
              <div className="divider-ou"><span>ou</span></div>
              <Button
                variant="secondary"
                className="w-100 google-signin-button"
                onClick={signInWithGoogle}
                disabled={loading}
              >
                <img src="/google-48.png" alt="Google icon" style={{ marginRight: '10px', width: '18px', height: '18px' }} />
                Se connecter avec Google
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;